// react
import { useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import {
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router';

// material ui icons
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';

// material ui
import { IconButton } from '@mui/material';

// api
import {
  useAlarmGroupsDeleteAsyncByidMutation,
  useAlarmGroupsGetAsyncByidQuery,
  useAlarmTypesGetCollectionAsyncByqueryOptionsQuery,
} from '@/api/generated/recalc/endpoints/v1/alarms';
import type { ReadAlarmGroupDto } from '@/api/generated/recalc/endpoints/v1/alarms';
import { useGetAccountByIdQuery } from '@/api/recalc/endpoints/accountsApiSlice';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';
import GatewayLoadingIndicator from '@/components/ui/progress/GatewayLoadingIndicator';

// feature-components
import DeleteAlarmGroupDialog from '@/features/alarm-hub/components/alarm-group/DeleteAlarmGroupDialog';
import EditAlarmGroupForm from '@/features/alarm-hub/components/alarm-group/forms/EditAlarmGroupForm';

// components
// Redux
import { showSnackbar } from '@/redux/snackbarSlice';

export const Route = createFileRoute(
  '/$accountId/_user/alarms/alarm-groups/$groupId',
)({
  component: RouteComponent,
  staticData: { crumb: 'alarms:alarmGroups.editGroup' },
});

function RouteComponent() {
  const { t } = useTranslation('alarms');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { groupId } = useParams({ strict: false });
  const { accountId } = useParams({ strict: false });
  const { data: account } = useGetAccountByIdQuery(accountId);

  const groupIdNumber = groupId ? Number(groupId) : undefined;

  const { data: alarmGroup, isLoading: isLoadingGroup } =
    useAlarmGroupsGetAsyncByidQuery(
      { id: groupIdNumber ?? 0 },
      { skip: !groupIdNumber },
    );

  const { data: alarmTypes = [], isLoading: isLoadingAlarmTypes } =
    useAlarmTypesGetCollectionAsyncByqueryOptionsQuery({
      orderby: 'AlarmTypeId',
      top: 10000,
    });

  // Local state for delete confirmation
  const [groupToDelete, setGroupToDelete] = useState<ReadAlarmGroupDto | null>(
    null,
  );

  // API mutations
  const [deleteAlarmGroup] = useAlarmGroupsDeleteAsyncByidMutation();

  // Event handlers
  const handleCancel = () => {
    navigate({ to: '..' });
  };

  const handleDeleteGroup = () => {
    if (alarmGroup) setGroupToDelete(alarmGroup);
  };

  const handleCancelDelete = () => {
    setGroupToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!groupToDelete) return;

    const deleteResult = await deleteAlarmGroup({
      id: groupToDelete.AlarmGroupId,
    });

    if (!('error' in deleteResult)) {
      dispatch(
        showSnackbar({
          message:
            t('messages.alarm_group_deleted') ||
            'Alarm group deleted successfully',
          severity: 'success',
        }),
      );
      navigate({ to: '..' });
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  return (
    <PageContainer maxWidth="xl" sx={{ py: 2 }}>
      <GatewayLoadingIndicator
        isLoading={isLoadingGroup || isLoadingAlarmTypes}
      />
      <PageHeader title={t('alarmGroups.editGroup', { ns: 'alarms' })}>
        <IconButton
          sx={{
            borderRadius: 2,
            ':hover': { color: 'error.main' },
          }}
          onClick={handleDeleteGroup}
        >
          <DeleteOutlineRoundedIcon />
        </IconButton>
      </PageHeader>

      {alarmGroup ? (
        <EditAlarmGroupForm
          key={alarmGroup.AlarmGroupId}
          alarmGroup={alarmGroup}
          userOptions={account?.UserRoles || []}
          alarmTypeOptions={alarmTypes}
          onSuccess={() => navigate({ to: '..' })}
          onCancel={handleCancel}
        />
      ) : null}

      <DeleteAlarmGroupDialog
        open={!!groupToDelete}
        groupToDelete={groupToDelete}
        users={account?.UserRoles || []}
        alarmTypes={alarmTypes}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </PageContainer>
  );
}
